/**
 * /login — Firebase Auth with THREE sign-in methods:
 *   1. Phone OTP (SMS) — original method, still primary for mobile users.
 *   2. Email Magic Link — passwordless; user gets a one-click link in inbox.
 *   3. Google Sign-in — one-click OAuth for anyone with a Gmail account.
 *
 * The page also handles the email-link callback: when a user clicks the magic
 * link in their inbox, they return here with `?email-link=1&oobCode=...`.
 * We detect it and complete the sign-in automatically.
 *
 * Test phones (Firebase Console-configured, no real SMS):
 *   +15550100000  → admin (auto-privileged)
 *   +15550100001, +15550100002, +15550100003 → QA
 *   OTP for all four is `123456`.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, ShieldCheck, ArrowLeft, Loader2, RefreshCw, Check, User, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { resolveSiteSlug, siteProfile } from "@/config/siteConfig";
import { beginSocialPulseLogin, socialPulseConfigured } from "@/lib/spOidc";

/**
 * Apply any registration-time data stashed by <QuickAuthModal /> before
 * it kicked off the email-link (or Google) round-trip. Runs only when
 * ``isNewUser`` is true so returning users' profiles aren't silently
 * overwritten. Idempotent — successive calls no-op after localStorage
 * is cleared.
 */
async function hydratePendingSignupData(isNewUser) {
  if (!isNewUser) {
    try {
      localStorage.removeItem("sp:pending-profile");
      localStorage.removeItem("sp:pending-business");
      localStorage.removeItem("sp:pending-policy-acceptance");
    } catch { /* noop */ }
    return;
  }
  // 1. Profile patch (name, gender, YOB, location) collected in Register modal.
  try {
    const raw = localStorage.getItem("sp:pending-profile");
    if (raw) {
      const patch = JSON.parse(raw);
      if (patch && typeof patch === "object" && Object.keys(patch).length) {
        try { await http.patch("/me/profile", patch); }
        catch (e) { console.warn("hydratePendingSignupData: profile PATCH failed", e?.response?.data || e?.message); }
      }
      localStorage.removeItem("sp:pending-profile");
    }
  } catch (e) { console.warn("hydratePendingSignupData: profile hydrate error", e); }

  // 2. Business account (Register-as-Business path).
  try {
    const raw = localStorage.getItem("sp:pending-business");
    if (raw) {
      const payload = JSON.parse(raw);
      if (payload?.name) {
        try { await http.post("/businesses", payload); }
        catch (e) { console.warn("hydratePendingSignupData: business POST failed", e?.response?.data || e?.message); }
      }
      localStorage.removeItem("sp:pending-business");
    }
  } catch (e) { console.warn("hydratePendingSignupData: business hydrate error", e); }

  // 3. Policy acceptance audit trail. Same idempotency semantics —
  // the backend accepts the same list twice and simply appends a
  // fresh log entry, so re-running this on refresh is safe.
  try {
    const raw = localStorage.getItem("sp:pending-policy-acceptance");
    if (raw) {
      const payload = JSON.parse(raw);
      if (payload?.policies?.length) {
        try {
          await http.post("/me/policy-acceptance", {
            ...payload,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          });
        } catch (e) {
          console.warn("hydratePendingSignupData: policy-acceptance POST failed", e?.response?.data || e?.message);
        }
      }
      localStorage.removeItem("sp:pending-policy-acceptance");
    }
  } catch (e) { console.warn("hydratePendingSignupData: policy hydrate error", e); }
}
import { isEmailLinkCallback, readStoredEmailForLink } from "@/lib/firebase";

const RESEND_COOLDOWN_SECONDS = 30;

export default function Login() {
  const {
    sendOtp, verifyOtp,
    sendEmailLink, verifyEmailLinkFromUrl,
    signInWithGoogleProvider,
  } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const [method, setMethod] = useState("phone"); // "phone" | "email"
  const [step, setStep] = useState("start");     // "start" | "otp" | "email-sent" | "email-verify"
  const [phone, setPhone] = useState("+1");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [trust, setTrust] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  // Site-aware branding — Food Pulse users need to see the FP name on
  // /login so it's obvious they're joining the food-network wing (even
  // though accounts are shared across every sibling site).
  const siteSlug = (() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      return resolveSiteSlug({ queryParam: q || "" });
    } catch { return "socialpulse"; }
  })();
  const isFoodPulse = siteSlug === "foodpulse";
  const brandLabel = isFoodPulse ? "Food Pulse" : siteProfile(siteSlug).label;
  const welcomeToast = isFoodPulse ? "Welcome to Food Pulse!" : "Welcome to Social Pulse!";

  // Post-auth destination priority:
  //   1. `?redirect=<internal-path>` query param (from guest-CTA links
  //      like "Join to post here" on a location article).
  //   2. `state.from` set by ProtectedRoute when the user was bounced
  //      here from a signed-in-only page.
  //   3. Home ("/").
  // We only trust internal paths ("/foo/bar") to prevent open-redirect
  // attacks — anything with a scheme or host is discarded.
  const redirectParam = new URLSearchParams(loc.search).get("redirect");
  const safeRedirect = redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
    ? redirectParam
    : null;
  const from = safeRedirect || loc.state?.from || "/";

  // -------- Cooldown timer helpers ---------
  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
  }, []);

  // -------- Email-link return handler ---------
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isEmailLinkCallback(window.location.href)) return;
    const stored = readStoredEmailForLink();
    setMethod("email");
    if (stored) {
      setEmail(stored);
      // Auto-complete when we already know the email (same device).
      (async () => {
        setBusy(true);
        try {
          const { is_new_user } = await verifyEmailLinkFromUrl(stored, false);
          await hydratePendingSignupData(is_new_user);
          toast.success(is_new_user ? welcomeToast : "Signed in.");
          nav(is_new_user && !safeRedirect ? "/profile/edit" : (safeRedirect || "/"), { replace: true });
        } catch (err) {
          toast.error(err?.message || "Couldn't complete sign-in.");
          setStep("email-verify");
        } finally { setBusy(false); }
      })();
    } else {
      // Different device — ask the user to re-enter their email.
      setStep("email-verify");
    }
  }, [verifyEmailLinkFromUrl, nav]);

  // -------- Utilities ---------
  const sanitizePhone = (raw) => {
    let s = (raw || "").replace(/[^\d+]/g, "");
    s = s.replace(/^\++/, "+").replace(/\+(?=.*\+)/g, "");
    if (!s.startsWith("+")) {
      if (s.length === 10) s = "+1" + s;
      else if (s.startsWith("1") && s.length === 11) s = "+" + s;
      else s = "+" + s;
    }
    return s;
  };

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

  const humanizeFirebaseError = (e) => {
    const code = e?.code || "";
    if (code.includes("invalid-phone-number")) return "That phone number isn't valid. Include country code.";
    if (code.includes("too-many-requests")) return "Too many attempts — try again in a few minutes.";
    if (code.includes("captcha")) return "reCAPTCHA failed — refresh and try again.";
    if (code.includes("quota-exceeded")) return "SMS quota exceeded — please try again later.";
    if (code.includes("operation-not-allowed")) return "This sign-in method isn't enabled yet for your region.";
    if (code.includes("popup-closed-by-user")) return "Sign-in cancelled.";
    if (code.includes("popup-blocked")) return "Popup blocked — allow popups and try again.";
    if (code.includes("invalid-email")) return "That email address doesn't look right.";
    if (code.includes("expired-action-code")) return "This link has expired — request a new one below (or switch to Google sign-in).";
    if (code.includes("invalid-action-code")) return "This link is invalid or already used. Magic links can only be opened once, from the same browser you requested them in. Request a new one below, or use Continue with Google.";
    if (code.includes("network-request-failed") || (e?.message || "").toLowerCase().includes("network")) {
      return "Network error — Firebase couldn't reach the phone-auth service. This usually means the current domain isn't in Firebase's Authorized domains list. Please try Continue with Google instead, or ask an admin to add this domain in Firebase Console → Authentication → Settings → Authorized domains.";
    }
    return e?.message || "Something went wrong — try again.";
  };

  // -------- Phone flow ---------
  const submitPhone = async (e) => {
    e.preventDefault();
    const clean = sanitizePhone(phone);
    const digits = clean.replace(/\D/g, "");
    if (!clean.startsWith("+") || digits.length < 8 || digits.length > 15) {
      toast.error("Please enter your full number with country code — e.g. +15550100000 (USA) or +919876543210 (India).");
      return;
    }
    setPhone(clean);
    setBusy(true);
    try {
      await sendOtp(clean);
      toast.success("Code sent — check your messages.");
      setStep("otp");
      setOtp("");
      startCooldown();
    } catch (err) {
      toast.error(humanizeFirebaseError(err));
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setOtp("");
    try {
      await sendOtp(phone);
      toast.success("New code sent — check your messages.");
      startCooldown();
    } catch (err) {
      toast.error(humanizeFirebaseError(err));
    } finally { setResending(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { toast.error("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const { is_new_user } = await verifyOtp(otp.trim(), trust);
      toast.success(is_new_user ? welcomeToast : "Signed in.");
      // New users: send to profile-edit UNLESS the caller asked for a
      // specific redirect (e.g. "Join to post here" wants them back on
      // the article page immediately).  Existing users always go to
      // `from` (redirect param → state.from → home).
      nav(is_new_user && !safeRedirect ? "/profile/edit" : from, { replace: true });
    } catch (err) {
      const code = err?.code || "";
      if (code.includes("invalid-verification-code")) toast.error("Wrong code — please check and try again.");
      else if (code.includes("code-expired")) toast.error("Code expired — tap Resend code to get a new one.");
      else toast.error(err?.response?.data?.detail || humanizeFirebaseError(err));
    } finally { setBusy(false); }
  };

  // -------- Email flow ---------
  const submitEmail = async (e) => {
    e.preventDefault();
    if (!validEmail(email)) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      await sendEmailLink(email.trim().toLowerCase());
      toast.success("Magic link sent — check your inbox.");
      setStep("email-sent");
    } catch (err) {
      toast.error(humanizeFirebaseError(err));
    } finally { setBusy(false); }
  };

  const submitEmailVerify = async (e) => {
    e.preventDefault();
    if (!validEmail(email)) { toast.error("Please enter the email you used."); return; }
    setBusy(true);
    try {
      const { is_new_user } = await verifyEmailLinkFromUrl(email.trim().toLowerCase(), trust);
      await hydratePendingSignupData(is_new_user);
      toast.success(is_new_user ? welcomeToast : "Signed in.");
      nav(is_new_user && !safeRedirect ? "/profile/edit" : from, { replace: true });
    } catch (err) {
      toast.error(humanizeFirebaseError(err));
    } finally { setBusy(false); }
  };

  // -------- Google flow ---------
  const submitGoogle = async () => {
    setBusy(true);
    try {
      const { is_new_user } = await signInWithGoogleProvider(trust);
      await hydratePendingSignupData(is_new_user);
      toast.success(is_new_user ? welcomeToast : "Signed in.");
      nav(is_new_user && !safeRedirect ? "/profile/edit" : from, { replace: true });
    } catch (err) {
      toast.error(humanizeFirebaseError(err));
    } finally { setBusy(false); }
  };

  // -------- Render ---------
  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <div id="firebase-recaptcha" />
      <div className="overline">SIGN IN</div>
      <h1 className="font-display text-4xl mt-2" data-testid="login-h1">Welcome to {brandLabel}<span style={{ color: "#B25D38" }}>{isFoodPulse ? "™" : ""}</span>.</h1>
      {isFoodPulse && (
        <div className="mt-1 text-xs font-mono uppercase tracking-widest" style={{ color: "#8B5E10" }} data-testid="login-fp-tagline">
          Food Pulse is a Division of Social Pulse<sup>®</sup>
        </div>
      )}
      <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Choose how you'd like to sign in — phone, email, or Google. No passwords, no forgot-password headaches.
      </p>

      {/* SocialPulse SSO + Google — always visible on the start step */}
      {step === "start" && (
        <>
          {socialPulseConfigured() && (
            <button
              type="button"
              onClick={() => beginSocialPulseLogin(new URLSearchParams(window.location.search).get("next") || "/")}
              disabled={busy}
              data-testid="login-socialpulse-btn"
              className="mt-8 w-full flex items-center justify-center gap-3 rounded-full px-4 py-3 text-sm font-semibold text-white hover:shadow-md transition disabled:opacity-60"
              style={{ background: "#7C3AED" }}
            >
              <ShieldCheck className="w-4 h-4" />
              Continue with SocialPulse
            </button>
          )}
          <button
            type="button"
            onClick={submitGoogle}
            disabled={busy}
            data-testid="login-google-btn"
            className={`${socialPulseConfigured() ? "mt-3" : "mt-8"} w-full flex items-center justify-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium hover:bg-black/[0.02] disabled:opacity-60 transition`}
            style={{ borderColor: "var(--wofy-border)" }}
          >
            <GoogleIcon />
            {busy ? "Signing in…" : "Continue with Google"}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            <div className="flex-1 h-px" style={{ background: "var(--wofy-border)" }} />
            <span className="font-mono uppercase tracking-widest">or</span>
            <div className="flex-1 h-px" style={{ background: "var(--wofy-border)" }} />
          </div>

          {/* Method toggle */}
          <div className="flex gap-2 mb-4" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={method === "phone"}
              data-testid="login-tab-phone"
              onClick={() => setMethod("phone")}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition"
              style={{
                borderColor: method === "phone" ? "var(--wofy-primary)" : "var(--wofy-border)",
                background: method === "phone" ? "var(--wofy-primary-soft, rgba(0,0,0,0.03))" : "transparent",
                color: method === "phone" ? "var(--wofy-primary)" : "var(--wofy-text)",
              }}
            >
              <Phone className="w-4 h-4" /> Phone
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={method === "email"}
              data-testid="login-tab-email"
              onClick={() => setMethod("email")}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition"
              style={{
                borderColor: method === "email" ? "var(--wofy-primary)" : "var(--wofy-border)",
                background: method === "email" ? "var(--wofy-primary-soft, rgba(0,0,0,0.03))" : "transparent",
                color: method === "email" ? "var(--wofy-primary)" : "var(--wofy-text)",
              }}
            >
              <Mail className="w-4 h-4" /> Email
            </button>
          </div>

          {method === "phone" && (
            <form onSubmit={submitPhone} className="space-y-4" data-testid="login-phone-form">
              <label className="block">
                <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                  Phone number
                </span>
                <div className="mt-1 flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }}>
                  <Phone className="w-4 h-4 opacity-50" />
                  <input
                    data-testid="login-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+15550100000"
                    className="flex-1 outline-none bg-transparent font-mono text-base"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
                <span className="mt-1 block text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                  Include the country code (e.g. +1 for USA, +91 for India). We'll text you a 6-digit code.
                </span>
              </label>

              <button
                data-testid="login-send-otp-btn"
                type="submit"
                disabled={busy}
                className="w-full wofy-btn-primary justify-center text-sm disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {busy ? "Sending…" : "Send code"}
              </button>
            </form>
          )}

          {method === "email" && (
            <form onSubmit={submitEmail} className="space-y-4" data-testid="login-email-form">
              <label className="block">
                <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                  Email address
                </span>
                <div className="mt-1 flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }}>
                  <Mail className="w-4 h-4 opacity-50" />
                  <input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 outline-none bg-transparent text-base"
                    autoComplete="email"
                  />
                </div>
                <span className="mt-1 block text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                  We'll email you a one-click sign-in link. No password required.
                </span>
              </label>

              <button
                data-testid="login-send-email-btn"
                type="submit"
                disabled={busy}
                className="w-full wofy-btn-primary justify-center text-sm disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {busy ? "Sending…" : "Send magic link"}
              </button>
            </form>
          )}

          <p className="text-xs text-center pt-6" style={{ color: "var(--wofy-text-soft)" }}>
            By continuing you agree to our <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </>
      )}

      {/* OTP step (phone flow) */}
      {step === "otp" && (
        <form onSubmit={submitOtp} className="mt-8 space-y-4" data-testid="login-otp-form">
          <button
            type="button"
            onClick={() => { setStep("start"); setOtp(""); }}
            className="wofy-btn-ghost text-xs"
            data-testid="login-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Change number
          </button>

          <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            Code sent to <span className="font-mono" style={{ color: "var(--wofy-text)" }}>{phone}</span>
          </p>

          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              6-digit code
            </span>
            <input
              data-testid="login-otp-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="w-full mt-1 rounded-lg border px-3 py-3 text-xl font-mono tracking-widest text-center outline-none"
              style={{ borderColor: "var(--wofy-border)" }}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
            />
          </label>

          <TrustCheckbox trust={trust} setTrust={setTrust} />

          <button
            data-testid="login-verify-btn"
            type="submit"
            disabled={busy || otp.length < 6}
            className="w-full wofy-btn-primary justify-center text-sm disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Verify and sign in"}
          </button>

          <div className="pt-2 flex items-center justify-center text-xs">
            <span style={{ color: "var(--wofy-text-soft)" }}>Didn't get it?&nbsp;</span>
            <button
              type="button"
              onClick={resendCode}
              disabled={cooldown > 0 || resending || busy}
              data-testid="login-resend-otp-btn"
              className="inline-flex items-center gap-1 underline disabled:opacity-50 disabled:no-underline"
              style={{ color: "var(--wofy-primary)" }}
            >
              {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {resending
                ? "Sending…"
                : cooldown > 0
                  ? `Resend code in ${cooldown}s`
                  : "Resend code"}
            </button>
          </div>
        </form>
      )}

      {/* Email-sent confirmation (before user clicks the link) */}
      {step === "email-sent" && (
        <div className="mt-8 space-y-4" data-testid="login-email-sent">
          <div className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="flex items-start gap-3">
              <div className="mt-1 rounded-full p-2" style={{ background: "var(--wofy-primary-soft, rgba(0,0,0,0.04))" }}>
                <Check className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              </div>
              <div>
                <p className="font-medium">Magic link on the way!</p>
                <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
                  We just sent a one-click sign-in link to{" "}
                  <span className="font-mono" style={{ color: "var(--wofy-text)" }}>{email}</span>.
                </p>
                <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>
                  Check your inbox (and spam). The link expires in 1 hour. Come back here after clicking it.
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStep("start")}
            className="wofy-btn-ghost text-xs"
            data-testid="login-email-change"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Use a different method
          </button>
        </div>
      )}

      {/* Email-verify step (when user clicks the magic link from a different device) */}
      {step === "email-verify" && (
        <form onSubmit={submitEmailVerify} className="mt-8 space-y-4" data-testid="login-email-verify-form">
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Almost there! Confirm the email you used to request this sign-in link.
          </p>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              Email address
            </span>
            <div className="mt-1 flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }}>
              <Mail className="w-4 h-4 opacity-50" />
              <input
                data-testid="login-email-verify-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 outline-none bg-transparent text-base"
                autoComplete="email"
                autoFocus
              />
            </div>
          </label>
          <TrustCheckbox trust={trust} setTrust={setTrust} />
          <button
            data-testid="login-email-verify-btn"
            type="submit"
            disabled={busy}
            className="w-full wofy-btn-primary justify-center text-sm disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {busy ? "Signing in…" : "Complete sign-in"}
          </button>
        </form>
      )}
    </div>
  );
}

// -------- Small helpers ---------

function TrustCheckbox({ trust, setTrust }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        data-testid="login-trust-checkbox"
        checked={trust}
        onChange={(e) => setTrust(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border"
        style={{ borderColor: "var(--wofy-border)", accentColor: "var(--wofy-primary)" }}
      />
      <span>
        <b>Trust this device for 30 days.</b>
        <span className="block text-xs mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
          Skip the OTP on this browser for the next month. Sign out anytime to end trust.
        </span>
      </span>
    </label>
  );
}

// Inline Google "G" logo — matches Google's brand guidelines for OAuth buttons.
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}
